/**
 * Copyright (c) 2019-2021 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author Alexander Rose <alexander.rose@weirdbyte.de>
 */
import { __assign } from "tslib";
import { createComputeRenderable } from '../../renderable';
import { createComputeRenderItem } from '../../webgl/render-item';
import { TextureSpec, UniformSpec } from '../../renderable/schema';
import { ShaderCode } from '../../../mol-gl/shader-code';
import { ValueCell } from '../../../mol-util';
import { QuadSchema, QuadValues } from '../util';
import { getHistopyramidSum } from './sum';
import { isPowerOfTwo } from '../../../mol-math/misc';
import { quad_vert } from '../../../mol-gl/shader/quad.vert';
import { reduction_frag } from '../../../mol-gl/shader/histogram-pyramid/reduction.frag';
import { isWebGL2 } from '../../webgl/compat';

var HistopyramidReductionSchema = __assign(__assign({}, QuadSchema), {
  tInputLevel: TextureSpec('texture', 'rgba', 'float', 'nearest'),
  tPreviousLevel: TextureSpec('texture', 'rgba', 'float', 'nearest'),
  uSize: UniformSpec('f'),
  uTexSize: UniformSpec('f'),
  uFirst: UniformSpec('b')
});

var HistogramPyramidName = 'histogram-pyramid';

function getHistopyramidReductionRenderable(ctx, inputLevel, previousLevel) {
  if (ctx.namedComputeRenderables[HistogramPyramidName]) {
    var v = ctx.namedComputeRenderables[HistogramPyramidName].values;
    ValueCell.update(v.tInputLevel, inputLevel);
    ValueCell.update(v.tPreviousLevel, previousLevel);
    ctx.namedComputeRenderables[HistogramPyramidName].update();
  } else {
    ctx.namedComputeRenderables[HistogramPyramidName] = createHistopyramidReductionRenderable(ctx, inputLevel, previousLevel);
  }

  return ctx.namedComputeRenderables[HistogramPyramidName];
}

function createHistopyramidReductionRenderable(ctx, inputLevel, previousLevel) {
  var values = __assign(__assign({}, QuadValues), {
    tInputLevel: ValueCell.create(inputLevel),
    tPreviousLevel: ValueCell.create(previousLevel),
    uSize: ValueCell.create(0),
    uTexSize: ValueCell.create(0),
    uFirst: ValueCell.create(true)
  });

  var schema = __assign({}, HistopyramidReductionSchema);

  var shaderCode = ShaderCode('reduction', quad_vert, reduction_frag, {}, {
    0: 'ivec4'
  });
  var renderItem = createComputeRenderItem(ctx, 'triangles', shaderCode, schema, values);
  return createComputeRenderable(renderItem, values);
}

var LevelTexturesFramebuffers = [];

function getLevelTextureFramebuffer(ctx, level) {
  var textureFramebuffer = LevelTexturesFramebuffers[level];
  var size = Math.pow(2, level);

  if (textureFramebuffer === undefined) {
    var texture = ctx.isWebGL2 ? getTexture("level" + level, ctx, 'image-int32', 'alpha', 'int', 'nearest') : getTexture("level" + level, ctx, 'image-uint8', 'rgba', 'ubyte', 'nearest');
    texture.define(size, size);
    var framebuffer = getFramebuffer("level" + level, ctx);
    texture.attachFramebuffer(framebuffer, 0);
    textureFramebuffer = {
      texture: texture,
      framebuffer: framebuffer
    };
    LevelTexturesFramebuffers[level] = textureFramebuffer;
  }

  return textureFramebuffer;
}

function setRenderingDefaults(ctx) {
  var gl = ctx.gl,
      state = ctx.state;
  state.disable(gl.CULL_FACE);
  state.disable(gl.BLEND);
  state.disable(gl.DEPTH_TEST);
  state.enable(gl.SCISSOR_TEST);
  state.depthMask(false);
  state.colorMask(true, true, true, true);
  state.clearColor(0, 0, 0, 0);
}

function getFramebuffer(name, webgl) {
  var _name = HistogramPyramidName + "-" + name;

  if (!webgl.namedFramebuffers[_name]) {
    webgl.namedFramebuffers[_name] = webgl.resources.framebuffer();
  }

  return webgl.namedFramebuffers[_name];
}

function getTexture(name, webgl, kind, format, type, filter) {
  var _name = HistogramPyramidName + "-" + name;

  if (!webgl.namedTextures[_name]) {
    webgl.namedTextures[_name] = webgl.resources.texture(kind, format, type, filter);
  }

  return webgl.namedTextures[_name];
}

export function createHistogramPyramid(ctx, inputTexture, scale, gridTexDim) {
  var gl = ctx.gl;
  var w = inputTexture.getWidth();
  var h = inputTexture.getHeight(); // printTexture(ctx, inputTexture, 2)

  if (w !== h || !isPowerOfTwo(w)) {
    throw new Error('inputTexture must be of square power-of-two size');
  } // This part set the levels


  var levels = Math.ceil(Math.log(w) / Math.log(2));
  var maxSize = Math.pow(2, levels);
  var maxSizeX = Math.pow(2, levels);
  var maxSizeY = Math.pow(2, levels - 1); // console.log('levels', levels, 'maxSize', maxSize, [maxSizeX, maxSizeY], 'input', w);

  var pyramidTex = ctx.isWebGL2 ? getTexture('pyramid', ctx, 'image-int32', 'alpha', 'int', 'nearest') : getTexture('pyramid', ctx, 'image-uint8', 'rgba', 'ubyte', 'nearest');
  pyramidTex.define(maxSizeX, maxSizeY);
  var framebuffer = getFramebuffer('pyramid', ctx);
  pyramidTex.attachFramebuffer(framebuffer, 0);
  gl.viewport(0, 0, maxSizeX, maxSizeY);

  if (isWebGL2(gl)) {
    gl.clearBufferiv(gl.COLOR, 0, [0, 0, 0, 0]);
  } else {
    gl.clear(gl.COLOR_BUFFER_BIT);
  }

  var levelTexturesFramebuffers = [];

  for (var i = 0; i < levels; ++i) levelTexturesFramebuffers.push(getLevelTextureFramebuffer(ctx, i));

  var renderable = getHistopyramidReductionRenderable(ctx, inputTexture, levelTexturesFramebuffers[0].texture);
  ctx.state.currentRenderItemId = -1;
  setRenderingDefaults(ctx);
  var offset = 0;

  for (var i = 0; i < levels; i++) {
    var currLevel = levels - 1 - i;
    var tf = levelTexturesFramebuffers[currLevel];
    tf.framebuffer.bind();
    var size = Math.pow(2, currLevel); // console.log('size', size, 'draw-level', currLevel, 'read-level', levels - i);

    ValueCell.update(renderable.values.uSize, Math.pow(2, i + 1) / maxSize);
    ValueCell.update(renderable.values.uTexSize, size);
    ValueCell.updateIfChanged(renderable.values.uFirst, i === 0);

    if (i > 0) {
      ValueCell.update(renderable.values.tPreviousLevel, levelTexturesFramebuffers[levels - i].texture);
      renderable.update();
    }

    ctx.state.currentRenderItemId = -1;
    gl.viewport(0, 0, size, size);
    gl.scissor(0, 0, size, size);

    if (isWebGL2(gl)) {
      gl.clearBufferiv(gl.COLOR, 0, [0, 0, 0, 0]);
    } else {
      gl.clear(gl.COLOR_BUFFER_BIT);
    }

    gl.scissor(0, 0, gridTexDim[0], gridTexDim[1]);
    renderable.render();
    pyramidTex.bind(0);
    gl.copyTexSubImage2D(gl.TEXTURE_2D, 0, offset, 0, 0, 0, size, size);
    pyramidTex.unbind(0);
    offset += size;
  }

  gl.finish(); // printTexture(ctx, pyramidTex, 2)
  //
  // return at least a count of one to avoid issues downstram

  var count = Math.max(1, getHistopyramidSum(ctx, levelTexturesFramebuffers[0].texture));
  var height = Math.ceil(count / Math.pow(2, levels)); // const scale = Vec2.create(maxSize / inputTexture.width, maxSize / inputTexture.height);
  // console.log('height', height, 'finalCount', count, 'scale', scale);

  return {
    pyramidTex: pyramidTex,
    count: count,
    height: height,
    levels: levels,
    scale: scale
  };
}